import React, { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Vector3 } from 'three';

import { isValidNumber } from '../../../../../../../utils/math/number';
import {
  fractionFromSlopePercent,
  vectorToSlopePercent,
} from '../../../../../../../utils/math/slope';
import { publish, SubscriptionTopic } from '../../../../../../../viewer/messaging/pubsub';
import { DesignTool, SnapMode } from '../../../../../../../viewer/tools/design-tool';
import { ViewSettings } from '../../../../../../../viewer/view-settings';
import { CheckBox } from '../../../../../check-box/check-box';
import { LabelledContainer } from '../../../../../labelled-container/labelled-container';
import { Stack } from '../../../../../stack/stack';
import { TextBox } from '../../../../../text-box/text-box';
import { CollapsableSection } from '../../../components/collapsable-section';
import { OptionLabels } from '../../../components/option-labels';
import { PositionInput } from '../../../components/position-input';

type Props = {
  expanded: boolean;
  tool: DesignTool;
};

const SnapValueTextBox = (title: string, value: string, onChanged: (value: string) => void) => {
  const { t } = useTranslation();
  return (
    <LabelledContainer key={title} text={title}>
      {(formElementId) => (
        <TextBox
          autoFocus={true}
          defaultValue={value}
          errorMessage={
            isValidNumber(parseFloat(value))
              ? undefined
              : t('section.snap.validation.invalidNumber', { ns: 'skyviewDesign' })
          }
          id={formElementId()}
          step={0.01}
          type="number"
          onChange={(e) => onChanged(e.target.value)}
        />
      )}
    </LabelledContainer>
  );
};

const SlopeRatioTextBox = (value: string, onChanged: (value: string) => void) => {
  const { t } = useTranslation();
  const parsedValue = parseFloat(value);
  const fraction = fractionFromSlopePercent(parsedValue);

  return (
    <PositionInput
      /* X is Height and X is width, so we flip it when we display it. */
      defaultValue={isValidNumber(parsedValue) ? { x: fraction.y, y: fraction.x, z: 0 } : undefined}
      display={[
        t('section.snap.relativeSlopeFractionButton.inputTitle.height', { ns: 'skyviewDesign' }),
        t('section.snap.relativeSlopeFractionButton.inputTitle.width', { ns: 'skyviewDesign' }),
      ]}
      onValueChanged={(point) => {
        const percent = vectorToSlopePercent(new Vector3(point.y, 0, point.x));
        onChanged(percent.toString());
      }}
    />
  );
};

export const SnapSection = (props: Props) => {
  const { t } = useTranslation();
  const [snapMode, setSnapMode] = React.useState(props.tool.snapMode);
  const [height, setHeight] = React.useState<string>(props.tool.snapValues.height.toString());
  const [relativeHeight, setRelativeHeight] = React.useState<string>(
    props.tool.snapValues.relativeHeight.toString(),
  );
  const [snapTowardAssets, setSnapTowardAssets] = React.useState(props.tool.snapValues.assets);
  const [snapTowardPointCloud, setSnapTowardPointCloud] = React.useState(
    props.tool.snapValues.pointCloud,
  );
  const [snapTowardTiledModel, setSnapTowardTiledModel] = React.useState(
    props.tool.snapValues.tiledModel,
  );
  const [slope, setSlope] = React.useState<string>(props.tool.snapValues.slope.toString());
  const [isCadViewer] = React.useState(ViewSettings.instance.isCadViewer);

  useEffect(() => {
    props.tool.onSnapValuesChanged({
      assets: snapTowardAssets,
      pointCloud: snapTowardPointCloud,
      tiledModel: snapTowardTiledModel,
    });
  }, [props.tool, snapTowardAssets, snapTowardPointCloud, snapTowardTiledModel]);

  useEffect(() => {
    setSnapTowardAssets(props.tool.snapValues.assets);
    setSnapTowardPointCloud(props.tool.snapValues.pointCloud);
    setSnapTowardTiledModel(props.tool.snapValues.tiledModel);
  }, [props.tool, props.tool.asset]);

  const onSnapModeSet = (newValue: SnapMode) => {
    setSnapMode(newValue);
    props.tool.onSnapModeChanged(newValue);
  };

  return (
    <CollapsableSection
      expanded={props.expanded}
      title={t('section.snap.title', { ns: 'skyviewDesign' })}
      titleUpperCase={true}
      onSetExpanded={(state) =>
        publish(SubscriptionTopic.DesignToolMenuStateChanged, { snapMode: state })
      }
    >
      <OptionLabels
        active={snapMode}
        itemRows={[
          [
            {
              title: isCadViewer
                ? t('section.snap.objectButton.text', { ns: 'skyviewDesign' })
                : t('section.snap.timelineButton.text', { ns: 'skyviewDesign' }),
              value: SnapMode.Timeline,
              onSelect: () => onSnapModeSet(SnapMode.Timeline),
              tooltipText: isCadViewer
                ? t('section.snap.objectButton.tooltip', { ns: 'skyviewDesign' })
                : t('section.snap.timelineButton.tooltip', { ns: 'skyviewDesign' }),
              content: isCadViewer ? undefined : (
                <LabelledContainer text={t('section.snap.snapTowards', { ns: 'skyviewDesign' })}>
                  <Stack spacing={0.5}>
                    <CheckBox
                      checked={snapTowardPointCloud}
                      onChange={() => setSnapTowardPointCloud((oldValue) => !oldValue)}
                    >
                      {t('snap.pointCloud', { ns: 'skyviewDesign' })}
                    </CheckBox>
                    <CheckBox
                      checked={snapTowardTiledModel}
                      onChange={() => setSnapTowardTiledModel((oldValue) => !oldValue)}
                    >
                      {t('snap.tiledModel', { ns: 'skyviewDesign' })}
                    </CheckBox>
                    <CheckBox
                      checked={snapTowardAssets}
                      onChange={() => setSnapTowardAssets((oldValue) => !oldValue)}
                    >
                      {t('snap.assets', { ns: 'skyviewDesign' })}
                    </CheckBox>
                  </Stack>
                </LabelledContainer>
              ),
            },
          ],
          [
            {
              title: t('section.snap.heightButton.text', { ns: 'skyviewDesign' }),
              value: SnapMode.AbsoluteHeight,
              onSelect: () => onSnapModeSet(SnapMode.AbsoluteHeight),
              tooltipText: t('section.snap.heightButton.tooltip', { ns: 'skyviewDesign' }),
              content: SnapValueTextBox(
                t('section.snap.heightButton.inputTitle', { ns: 'skyviewDesign' }) as string,
                height,
                (input) => {
                  setHeight(input);
                  const value = parseFloat(input);
                  if (isValidNumber(value)) {
                    props.tool.onSnapValuesChanged({
                      height: value,
                    });
                  }
                },
              ),
            },
            {
              title: t('section.snap.relativeHeightButton.text', { ns: 'skyviewDesign' }),
              value: SnapMode.RelativeHeight,
              onSelect: () => onSnapModeSet(SnapMode.RelativeHeight),
              tooltipText: (
                <Trans
                  components={{ br: <br /> }}
                  i18nKey="section.snap.relativeHeightButton.tooltip"
                  ns="skyviewDesign"
                />
              ),
              content: SnapValueTextBox(
                t('section.snap.relativeHeightButton.inputTitle', {
                  ns: 'skyviewDesign',
                }) as string,
                relativeHeight,
                (input) => {
                  setRelativeHeight(input);
                  const value = parseFloat(input);
                  if (isValidNumber(value)) {
                    props.tool.onSnapValuesChanged({
                      relativeHeight: value,
                    });
                  }
                },
              ),
            },
          ],
          [
            {
              title: t('section.snap.relativeSlopePercentButton.text', { ns: 'skyviewDesign' }),
              value: SnapMode.RelativeSlopePercent,
              onSelect: () => onSnapModeSet(SnapMode.RelativeSlopePercent),
              tooltipText: (
                <Trans
                  components={{ br: <br /> }}
                  i18nKey="section.snap.relativeSlopePercentButton.tooltip"
                  ns="skyviewDesign"
                />
              ),
              content: SnapValueTextBox(
                t('section.snap.relativeSlopePercentButton.inputTitle', {
                  ns: 'skyviewDesign',
                }) as string,
                slope,
                (input) => {
                  setSlope(input);
                  const value = parseFloat(input);
                  if (isValidNumber(value)) {
                    props.tool.onSnapValuesChanged({
                      slope: value,
                    });
                  }
                },
              ),
            },
            {
              title: t('section.snap.relativeSlopeFractionButton.text', { ns: 'skyviewDesign' }),
              value: SnapMode.RelativeSlopeFraction,
              onSelect: () => onSnapModeSet(SnapMode.RelativeSlopeFraction),
              tooltipText: (
                <Trans
                  components={{ br: <br /> }}
                  i18nKey="section.snap.relativeSlopeFractionButton.tooltip"
                  ns="skyviewDesign"
                />
              ),
              content: SlopeRatioTextBox(slope, (input) => {
                setSlope(input);
                const value = parseFloat(input);
                if (isValidNumber(value)) {
                  props.tool.onSnapValuesChanged({
                    slope: value,
                  });
                }
              }),
            },
          ],
        ]}
      />
    </CollapsableSection>
  );
};
